import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { doctors } from '../data/mockData';
import { MessageSquare, Clock, Star, Shield, Search, Filter } from 'lucide-react';

const allDoctors = [
  ...doctors.generalCare,
  ...doctors.lifestyle,
  ...doctors.specialists
];

const specialties = [
  "All",
  "General Care",
  "Mental Health",
  "Nutrition",
  "Fitness",
  "Cardiology",
  "Neurology",
  "Orthopedics"
];

const DoctorCard = ({ doctor }) => {
  return (
    <div className="group relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100">
      {/* Image Container */}
      <div className="relative h-72 overflow-hidden">
        <img
          src={doctor.image}
          alt={doctor.name}
          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Online Badge */}
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center gap-1.5 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            Online
          </span>
        </div>

        {/* Quick Action on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Link to={`/chat/${encodeURIComponent(doctor.name)}`}>
            <Button className="bg-white/90 hover:bg-white text-[#166534] shadow-xl">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat Now
            </Button>
          </Link>
        </div>

        {/* Doctor Info */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h4 className="text-xl font-bold text-white mb-1">{doctor.name}</h4>
          <p className="text-green-300 text-sm font-medium">{doctor.specialty}</p>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6">
        <p className="text-gray-600 text-sm mb-4">
          {doctor.description || "Expert AI-powered healthcare guidance with evidence-based recommendations."}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5 text-gray-500 text-sm">
            <Clock className="w-4 h-4" />
            <span>{doctor.experience || "10+ years"}</span>
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="w-4 h-4 text-amber-400 fill-current" />
            ))}
            <span className="text-sm text-gray-600 ml-1">(4.9)</span>
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mb-5">
          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full">
            <Shield className="w-3 h-3" />
            AHPRA/HIPAA Compliant
          </span>
          <span className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full">
            24/7 Available
          </span>
          <span className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full">
            AI Powered
          </span>
        </div>

        {/* Action Button */}
        <Link to={`/chat/${encodeURIComponent(doctor.name)}`} className="block">
          <Button className="w-full bg-[#166534] hover:bg-[#14532d] text-white py-5 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Start Consultation
          </Button>
        </Link>
      </div>
    </div>
  );
};

const TeamPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');

  const filteredDoctors = allDoctors.filter(doctor => {
    const matchesSearch = doctor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          doctor.specialty.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpecialty = selectedSpecialty === 'All' || 
                             doctor.specialty.toLowerCase().includes(selectedSpecialty.toLowerCase());
    return matchesSearch && matchesSpecialty;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#166534] to-[#14532d] py-24 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full text-white text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              9 AI Doctors Available
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Meet Our AI Doctors
            </h1>
            <p className="text-xl text-green-100 max-w-2xl mx-auto mb-10">
              Expert virtual healthcare professionals powered by advanced AI, available 24/7 
              to provide evidence-based medical guidance.
            </p>

            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name or specialty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg rounded-2xl border-0 shadow-xl focus:ring-2 focus:ring-green-400"
              />
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
              <div className="flex items-center gap-2 text-gray-500 flex-shrink-0">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filter:</span>
              </div>
              {specialties.map((specialty) => (
                <button
                  key={specialty}
                  onClick={() => setSelectedSpecialty(specialty)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedSpecialty === specialty
                      ? 'bg-[#166534] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {specialty}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Doctors Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredDoctors.length > 0 ? (
              <>
                <p className="text-gray-600 mb-8">
                  Showing {filteredDoctors.length} doctor{filteredDoctors.length !== 1 ? 's' : ''}
                </p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredDoctors.map((doctor) => (
                    <DoctorCard key={doctor.id} doctor={doctor} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg mb-4">No doctors found matching your criteria.</p>
                <Button
                  variant="outline"
                  onClick={() => { setSearchQuery(''); setSelectedSpecialty('All'); }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-50 to-blue-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Need Immediate Assistance?
            </h2>
            <p className="text-gray-600 mb-8">
              Our AI doctors are available around the clock. Start a consultation now 
              and get expert healthcare guidance within seconds.
            </p>
            <Link to="/dashboard">
              <Button className="bg-[#166534] hover:bg-[#14532d] text-white px-8 py-6 text-lg rounded-xl">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TeamPage;
